import React, { useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import './office_panel.css';
import './consultas_ia.css';
import './conflictos.css';

const CAMPOS = 'id nombre email motivo detalleMotivo origen fechaNacimiento creado cuentaEmail cuentaNombre cuentaDashboard perfiles charlas otrasCuentas';
const LISTA = gql`query { getConflictosEmail { ${CAMPOS} } }`;
const FICHA = gql`query F($id: ID!) { getConflictoEmail(id: $id) { ${CAMPOS} activa resuelto perfilesChat { id nombre activo creado } otras { id nombre cuentaEmail perfiles charlas } } }`;
const CORREGIR = gql`mutation C($id: ID!, $email: String!, $tambienEn: [ID!]) { corregirEmailPersona(id: $id, email: $email, tambienEn: $tambienEn) { ok email personas perfiles charlas } }`;
const SACAR = gql`mutation S($id: ID!) { deactivateUserLink(id: $id) }`;

const fecha = (v) => (v ? new Date(v).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—');
const nacimiento = (v) => { const m = /^(\d{2})(\d{2})(\d{4})$/.exec(v || ''); if (m) return `${m[1]}/${m[2]}/${m[3]}`; const i = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v || ''); return i ? `${i[3]}/${i[2]}/${i[1]}` : (v && v !== 'pendiente' ? v : 'Pendiente'); };
const cuenta = (c) => [c.cuentaNombre, c.cuentaEmail].filter(Boolean).join(' · ') || '—';
const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;

function Marco({ titulo, volver, children }) {
  const { isSuperadmin, logout } = useAuth();
  const navigate = useNavigate();
  if (!isSuperadmin) {
    return (
      <div className="page-container" style={{ maxWidth: 900 }}>
        <div className="panel-card" style={{ padding: 40, textAlign: 'center' }}>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: 8 }}>Acceso denegado</h2>
          <p style={{ color: '#6b7280', marginBottom: 16 }}>Solo el superadmin puede acceder.</p>
          <button className="btn-accent" onClick={() => navigate('/altaEmpresa')}>Volver</button>
        </div>
      </div>
    );
  }
  return (
    <div className="page-container cia cf" style={{ maxWidth: 1100 }}>
      <div className="office-header">
        <div className="office-header-left">
          <button className="btn-subtle" onClick={() => navigate(volver)}>← Volver</button>
          <h1 style={{ fontSize: '1.3rem', fontWeight: 700, letterSpacing: '-0.3px', margin: 0 }}>{titulo}</h1>
          <span className="tag tag--purple">emails a corregir</span>
        </div>
        <button className="btn-subtle" onClick={() => logout()}>Cerrar sesión</button>
      </div>
      {children}
    </div>
  );
}

// Pestaña Conflictos: personas cuyo email no sirve para identificarlas.
export default function Conflictos() {
  const { id } = useParams();
  return id ? <Ficha id={id} /> : <Listado />;
}

function Listado() {
  const navigate = useNavigate();
  const { data, loading, error } = useQuery(LISTA, { fetchPolicy: 'network-only' });
  const xs = (data && data.getConflictosEmail) || [];
  const inventados = xs.filter((x) => x.motivo === 'Email inventado').length;
  return (
    <Marco titulo="Conflictos" volver="/altaEmpresa">
      <p className="cf-intro">
        Personas de los paneles cuyo email no sirve para identificarlas: los inventados (<code>nombre.apellido@manual.nawi</code>) que
        el alta manual generaba cuando el email quedaba vacío, y los que llegaron mal escritos. NAWI Chat liga a cada persona con sus
        perfiles y conversaciones por el email. Tocá una para cargar su email real o sacarla del panel.
      </p>
      {error && <div className="cs-aviso cs-aviso--error">{error.graphQLErrors?.[0]?.message || error.message}</div>}
      {loading && <div className="panel-card cf-vacio">Buscando conflictos…</div>}
      {!loading && !error && (
        <>
          <div className="cia-kpis cf-kpis">
            <div className="panel-card cia-kpi cia-kpi--principal"><span className="cia-kpi-titulo">Personas en conflicto</span><span className="cia-kpi-valor">{xs.length}</span></div>
            <div className="panel-card cia-kpi"><span className="cia-kpi-titulo">Email inventado</span><span className="cia-kpi-valor">{inventados}</span></div>
            <div className="panel-card cia-kpi"><span className="cia-kpi-titulo">Mal escrito o temporal</span><span className="cia-kpi-valor">{xs.length - inventados}</span></div>
            <div className="panel-card cia-kpi"><span className="cia-kpi-titulo">Conversaciones en NAWI Chat</span><span className="cia-kpi-valor">{xs.reduce((a, x) => a + x.charlas, 0)}</span></div>
          </div>
          {!xs.length ? <div className="panel-card cf-vacio">No hay conflictos: todas las personas tienen un email válido.</div> : (
            <div className="panel-card">
              <div className="cia-tabla-scroll">
                <table className="cia-tabla">
                  <thead>
                    <tr><th>Persona</th><th>Email</th><th>Motivo</th><th>Cuenta</th><th className="num">Perfiles</th><th className="num">Charlas</th><th className="num">Otras cuentas</th></tr>
                  </thead>
                  <tbody>
                    {xs.map((x) => (
                      <tr key={x.id} tabIndex={0} onClick={() => navigate(`/conflictos/${x.id}`)} onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/conflictos/${x.id}`); }}>
                        <td><b>{x.nombre}</b></td>
                        <td className="cf-email">{x.email}</td>
                        <td><span className={`cf-motivo ${x.motivo === 'Email inventado' ? 'cf-motivo--inventado' : ''}`}>{x.motivo}</span></td>
                        <td>{cuenta(x)}</td>
                        <td className="num">{x.perfiles}</td>
                        <td className="num">{x.charlas}</td>
                        <td className="num">{x.otrasCuentas || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </Marco>
  );
}

function Ficha({ id }) {
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useQuery(FICHA, { variables: { id }, fetchPolicy: 'network-only' });
  const [email, setEmail] = useState('');
  const [tambien, setTambien] = useState([]);
  const [confirmar, setConfirmar] = useState(null);   // 'email' | 'sacar'
  const [aviso, setAviso] = useState(null);
  const [corregir, { loading: guardando }] = useMutation(CORREGIR);
  const [sacar, { loading: sacando }] = useMutation(SACAR);
  const p = data && data.getConflictoEmail;
  const elegidas = p ? p.otras.filter((o) => tambien.includes(o.id)) : [];

  const guardar = async () => {
    setAviso(null);
    try {
      const r = (await corregir({ variables: { id, email: email.trim(), tambienEn: tambien } })).data.corregirEmailPersona;
      setConfirmar(null);
      setAviso({ tipo: 'ok', texto: `Listo: ${r.email} quedó en ${plural(r.personas, 'persona', 'personas')}, ${plural(r.perfiles, 'perfil', 'perfiles')} y ${plural(r.charlas, 'conversación', 'conversaciones')} de NAWI Chat.` });
      await refetch();
    } catch (e) { setConfirmar(null); setAviso({ tipo: 'error', texto: e.graphQLErrors?.[0]?.message || e.message }); }
  };
  const darDeBaja = async () => {
    setAviso(null);
    try {
      const ok = (await sacar({ variables: { id } })).data.deactivateUserLink;
      if (!ok) throw new Error('No se pudo sacar a la persona del panel.');
      navigate('/conflictos');
    } catch (e) { setConfirmar(null); setAviso({ tipo: 'error', texto: e.graphQLErrors?.[0]?.message || e.message }); }
  };

  return (
    <Marco titulo={p ? p.nombre : 'Conflicto'} volver="/conflictos">
      {error && <div className="cs-aviso cs-aviso--error">{error.graphQLErrors?.[0]?.message || error.message}</div>}
      {loading && <div className="panel-card cf-vacio">Cargando…</div>}
      {aviso && <div className={`cs-aviso ${aviso.tipo === 'error' ? 'cs-aviso--error' : 'cs-aviso--ok'}`}>{aviso.texto}</div>}
      {p && (
        <>
          <div className="cia-kpis cf-kpis">
            <div className={`panel-card cia-kpi ${p.resuelto ? 'cf-kpi--ok' : 'cf-kpi--conflicto'}`}>
              <span className="cia-kpi-titulo">Estado</span>
              <span className="cia-kpi-valor cf-estado">{!p.activa ? 'Fuera del panel' : p.resuelto ? 'Resuelto' : 'En conflicto'}</span>
              <span className="cia-kpi-pie">{p.resuelto ? 'El email ya es válido.' : p.detalleMotivo}</span>
            </div>
            <div className="panel-card cia-kpi"><span className="cia-kpi-titulo">Perfiles en NAWI Chat</span><span className="cia-kpi-valor">{p.perfiles}</span><span className="cia-kpi-pie">de esta cuenta</span></div>
            <div className="panel-card cia-kpi"><span className="cia-kpi-titulo">Conversaciones</span><span className="cia-kpi-valor">{p.charlas}</span><span className="cia-kpi-pie">ligadas por este email</span></div>
            <div className="panel-card cia-kpi"><span className="cia-kpi-titulo">Otras cuentas</span><span className="cia-kpi-valor">{p.otras.length}</span><span className="cia-kpi-pie">con el mismo email</span></div>
          </div>

          <div className="cf-columnas">
            <div className="panel-card cf-card">
              <div className="detail-section-title">Persona</div>
              <dl className="cf-datos">
                <dt>Nombre</dt><dd>{p.nombre}</dd>
                <dt>Email</dt><dd className="cf-email">{p.email} <span className={`cf-motivo ${p.motivo === 'Email inventado' ? 'cf-motivo--inventado' : ''}`}>{p.resuelto ? 'Válido' : p.motivo}</span></dd>
                <dt>Fecha de nacimiento</dt><dd>{nacimiento(p.fechaNacimiento)}</dd>
                <dt>Origen</dt><dd>{p.origen || '—'}</dd>
                <dt>Cargada el</dt><dd>{fecha(p.creado)}</dd>
                <dt>Cuenta</dt><dd>{cuenta(p)}{p.cuentaDashboard ? ` · dashboard ${p.cuentaDashboard}` : ''}</dd>
              </dl>
              {p.perfilesChat.length > 0 && (
                <>
                  <div className="detail-section-title">Perfiles en NAWI Chat</div>
                  <ul className="cf-perfiles">
                    {p.perfilesChat.map((x) => <li key={x.id}><b>{x.nombre}</b><span>{x.activo ? 'activo' : 'archivado'} · creado {fecha(x.creado)}</span></li>)}
                  </ul>
                </>
              )}
            </div>

            {p.activa && !p.resuelto && (
              <div className="cf-acciones">
                <div className="panel-card cf-card">
                  <div className="detail-section-title">Cargar el email real</div>
                  <p className="cf-texto">Se cambia en la persona y, en la misma cuenta, en sus perfiles y conversaciones de NAWI Chat, para que no queden huérfanas. Tiene que ser un email real: no se aceptan temporales ni mal escritos.</p>
                  {/^Jugador de Nawaiam/.test(p.origen || '') && <p className="cf-texto cf-texto--aviso">Viene del dashboard de Nawaiam: corregilo también allá, o la próxima actualización del panel la vuelve a traer con el email mal escrito.</p>}
                  <input className="manual-input cf-input" type="email" placeholder="email real de la persona" value={email} onChange={(e) => { setEmail(e.target.value); setConfirmar(null); }} />
                  {p.otras.length > 0 && (
                    <div className="cf-otras">
                      <span className="cf-texto">El mismo email inventado está en {plural(p.otras.length, 'otra cuenta', 'otras cuentas')}. Marcá las que son la misma persona:</span>
                      {p.otras.map((o) => (
                        <label key={o.id} className="cf-otra">
                          <input type="checkbox" checked={tambien.includes(o.id)} onChange={(e) => { setConfirmar(null); setTambien(e.target.checked ? [...tambien, o.id] : tambien.filter((x) => x !== o.id)); }} />
                          <span><b>{o.cuentaEmail || 'sin cuenta'}</b> · {o.nombre} · {plural(o.perfiles, 'perfil', 'perfiles')}, {plural(o.charlas, 'conversación', 'conversaciones')}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  {confirmar !== 'email' ? (
                    <button className="btn-green" disabled={!email.trim() || guardando} onClick={() => setConfirmar('email')}>Guardar email</button>
                  ) : (
                    <div className="cf-confirmar">
                      <span>Se cambia <b>{p.email}</b> por <b>{email.trim()}</b> en {plural(1 + elegidas.length, 'cuenta', 'cuentas')}: {plural(p.perfiles + elegidas.reduce((a, o) => a + o.perfiles, 0), 'perfil', 'perfiles')} y {plural(p.charlas + elegidas.reduce((a, o) => a + o.charlas, 0), 'conversación', 'conversaciones')} de NAWI Chat.</span>
                      <div className="cf-botones">
                        <button className="btn-outline" onClick={() => setConfirmar(null)} disabled={guardando}>Cancelar</button>
                        <button className="btn-green" onClick={guardar} disabled={guardando}>{guardando ? 'Guardando…' : 'Confirmar'}</button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="panel-card cf-card">
                  <div className="detail-section-title">Eliminar</div>
                  <p className="cf-texto">Saca a la persona del panel de {p.cuentaEmail || 'la cuenta'}, como cualquier baja de Office: se puede volver a agregar. Sus perfiles y conversaciones de NAWI Chat no se borran.</p>
                  {confirmar !== 'sacar' ? (
                    <button className="btn-danger-outline" disabled={sacando} onClick={() => setConfirmar('sacar')}>Sacar del panel</button>
                  ) : (
                    <div className="cf-confirmar">
                      <span>¿Sacar a <b>{p.nombre}</b> del panel?</span>
                      <div className="cf-botones">
                        <button className="btn-outline" onClick={() => setConfirmar(null)} disabled={sacando}>Cancelar</button>
                        <button className="btn-danger" onClick={darDeBaja} disabled={sacando}>{sacando ? 'Sacando…' : 'Sí, sacar'}</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </Marco>
  );
}
